import {
  useGetActivityMetricsQuery,
  useGetRecoveryConfigQuery,
} from 'api/recoveryAi'
import type { BrandVoiceMode, PhilosophyPosture } from 'api/recoveryAi.types'
import { useMemo } from 'react'
import { isRecoveryActive, isRecoveryConfigured } from '../recoveryStatus'

// The config query's lifecycle as ONE exhaustive state, so no call site can forget
// the "stale data in flight" middle state (two independent booleans made that state
// representable-but-unhandled). See ConfigSummary.configState.
export type ConfigState = 'cold' | 'refetching' | 'settled' | 'error'

// Collapse RTK's overlapping query flags into one lifecycle state. Early-return
// order is load-bearing: cold (no data yet) and error take precedence over the
// in-flight/settled split, so a first-load OR a failed GET never reads as settled.
const deriveConfigState = (q: {
  isUninitialized: boolean
  isLoading: boolean
  isFetching: boolean
  isError: boolean
}): ConfigState => {
  if (q.isUninitialized || q.isLoading) return 'cold'
  if (q.isError) return 'error'
  if (q.isFetching) return 'refetching'
  return 'settled'
}

// The per-card setting values shown on the AI-22 ConfiguredSummary page, derived
// from the LIVE recovery config + activity metrics (NOT interim constants — these
// are real, already-persisted settings). Every field is `null`/`false` until its
// source loads, so the page renders a graceful "unset" label rather than a
// fabricated value. Distinct from useActivationSummary (which owns the
// credit/header/billing bits shared with the success page) so the success page
// doesn't pull the config it never renders.
export interface ConfigSummary {
  philosophyPosture: PhilosophyPosture | null
  brandVoiceMode: BrandVoiceMode | null
  // N = enabled per-location configs (mirrors useActivationSummary's source).
  locationsActive: number | null
  approvedRewardCount: number | null
  notifyLocationManagers: boolean
  notifyAdmins: boolean
  // Onboarding-complete? Drives ConfiguredSummary's entry guard (a not-yet-onboarded
  // merchant must not reach the audited activate CTA — see ConfiguredSummary).
  isConfigured: boolean
  // Configured AND running (shared recoveryStatus predicate). ConfiguredSummary's
  // entry guard also bounces an already-active merchant so the audited PUT can't
  // re-fire on a live agent with no provisioning to show for it (AI-580).
  isActive: boolean
  // The config query lifecycle as one exhaustive state. ConfiguredSummary switches
  // on it (all flag-gated, so flag-OFF is unaffected):
  //   cold       → blank the page (no data yet). A background refetch is NOT cold, so
  //                it never rips the page (and an open modal) out of the DOM (#1 fix).
  //   refetching → keep the page mounted but treat as unsettled (stale data in flight).
  //   settled    → safe to evaluate the AI-695 redirect / enable the activate CTA.
  //   error      → surface a recoverable retry region, NOT a redirect that would derive
  //                isConfigured:false and bounce a configured merchant into onboarding.
  configState: ConfigState
  // Re-runs the failed config GET from the error region (stable RTK ref).
  refetch: () => void
}

export const useConfigSummary = (): ConfigSummary => {
  const {
    data: config,
    isFetching,
    isLoading,
    isUninitialized,
    isError,
    refetch,
  } = useGetRecoveryConfigQuery()
  const { data: metrics } = useGetActivityMetricsQuery()

  // Memoize so the returned object reference is stable between renders when neither
  // query has updated — ConfiguredSummary places this in a useMemo dependency array,
  // and a fresh object every render would defeat that memoization.
  return useMemo(
    () => ({
      philosophyPosture: config?.philosophy_posture ?? null,
      brandVoiceMode: config?.brand_voice_mode ?? null,
      locationsActive: metrics?.activeLocationsCount ?? null,
      // `?? null` (not `?? 0`): a missing config must render "rewards selected", not
      // a misleading "0 grantable rewards".
      approvedRewardCount: config?.approved_reward_ids?.length ?? null,
      notifyLocationManagers:
        config?.escalation_routing_notify_location_managers ?? false,
      notifyAdmins: config?.notify_admins ?? false,
      isConfigured: isRecoveryConfigured(config),
      isActive: isRecoveryActive(config),
      // isLoading (RTK's first-load-only flag) drives 'cold'; a background refetch over
      // stale data is isFetching-true / isLoading-false → 'refetching', so the page does
      // NOT blank mid-refetch (the #1 flicker). isError → 'error' so a failed GET surfaces
      // a retry region instead of fail-closing into the onboarding funnel (#4).
      configState: deriveConfigState({
        isUninitialized,
        isLoading,
        isFetching,
        isError,
      }),
      refetch,
    }),
    [config, metrics, isUninitialized, isFetching, isLoading, isError, refetch]
  )
}
